import { fixed, trim } from '../../math/format';
import { bilateralLimits, fit, inspect, limits } from '../../math/tolerance';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

export const TOL_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'tol.unilateral',
    topicId: 'tol',
    skill: 'Limits from a unilateral callout',
    gen: (rng) => {
      const nominal = rng.step(0.5, 2.5, 0.125);
      const upper = rng.pick([0.001, 0.002, 0.003, 0.005]);
      const solved = limits(nominal, upper, 0);
      const { min, max } = solved.value;
      const correct = `${fixed(min, 4)}" to ${fixed(max, 4)}"`;

      return {
        kind: 'mc',
        prompt: `${fixed(nominal, 3)}" +${trim(upper, 4).replace(/^0/, '')}/−.000. What are the limits?`,
        formula: 'max = nominal + upper · min = nominal − lower',
        why: `Nothing comes off the bottom, so ${fixed(nominal, 4)}" is the smallest legal size. ${trim(upper, 4)}" is allowed on top.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: `${fixed(nominal - upper, 4)}" to ${fixed(nominal, 4)}"`,
            trap: 'You put the tolerance on the wrong side. The plus sign means it is allowed above nominal, not below.',
          },
          {
            label: `${fixed(nominal - upper, 4)}" to ${fixed(max, 4)}"`,
            trap: 'You applied the tolerance both ways. The lower limit here is zero — nothing comes off the bottom.',
          },
          {
            label: `${fixed(nominal, 4)}" to ${fixed(nominal + upper / 2, 4)}"`,
            trap: 'You halved the tolerance. Unilateral means the whole amount goes on one side.',
          },
        ]),
      };
    },
  },
  {
    id: 'tol.total',
    topicId: 'tol',
    skill: 'Total tolerance from a ± callout',
    gen: (rng) => {
      const nominal = rng.step(0.5, 4, 0.25);
      const half = rng.pick([0.001, 0.002, 0.005, 0.01]);
      const solved = bilateralLimits(nominal, half);
      const correct = `${fixed(half * 2, 4)}"`;

      return {
        kind: 'mc',
        prompt: `${fixed(nominal, 3)}" ±${trim(half, 4).replace(/^0/, '')}. How much total tolerance?`,
        formula: 'total = upper + lower',
        why: `${trim(half, 4)}" each way, so the band runs ${fixed(solved.value.min, 4)}" to ${fixed(solved.value.max, 4)}" — ${fixed(half * 2, 4)}" wide.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: `${fixed(half, 4)}"`,
            trap: 'That is one side of the band. ± means you get that much in both directions, so the band is double.',
          },
          {
            label: `${fixed(half * 4, 4)}"`,
            trap: 'Doubled twice. ± gives you two sides, not four.',
          },
          {
            label: `${fixed(half / 2, 4)}"`,
            trap: 'You halved it. The number on the print is already one side of the band.',
          },
        ]),
      };
    },
  },
  {
    id: 'tol.inspect',
    topicId: 'tol',
    skill: 'Judging a measured part',
    gen: (rng) => {
      const nominal = rng.step(0.75, 2, 0.125);
      const half = rng.pick([0.0005, 0.001, 0.002]);
      const band = bilateralLimits(nominal, half).value;
      const outcome = rng.pick(['good', 'under', 'over'] as const);

      const measured =
        outcome === 'good'
          ? Math.round((nominal + rng.pick([-0.5, -0.2, 0, 0.3, 0.6]) * half) * 10000) / 10000
          : outcome === 'under'
            ? Math.round((band.min - rng.pick([0.0005, 0.001, 0.0017])) * 10000) / 10000
            : Math.round((band.max + rng.pick([0.0005, 0.001, 0.0017])) * 10000) / 10000;

      const solved = inspect(measured, band);
      const verdict = solved.value.verdict;
      // One plain label per verdict, shared by the correct answer and the
      // distractors. Two options that both read "scrap, undersize" would make
      // the question unanswerable rather than hard.
      const labels = {
        good: 'Good part',
        undersize: 'Scrap, undersize',
        oversize: 'Oversize, needs another cut',
      } as const;

      const wrong = (['good', 'undersize', 'oversize'] as const).filter((v) => v !== verdict);

      return {
        kind: 'mc',
        prompt: `Print says ${fixed(nominal, 4)}" ±${trim(half, 4).replace(/^0/, '')}. Part measures ${fixed(measured, 4)}". Verdict?`,
        formula: 'max = nominal + upper · min = nominal − lower',
        why: `Limits are ${fixed(band.min, 4)}" to ${fixed(band.max, 4)}". At ${fixed(measured, 4)}" the part is ${verdict === 'good' ? 'inside the band' : `${fixed(solved.value.outBy, 4)}" outside it`}.`,
        steps: solved.steps,
        ...mcOptions(rng, labels[verdict], [
          ...wrong.map((v) => ({
            label: labels[v],
            trap: `Work the limits out first: ${fixed(band.min, 4)}" to ${fixed(band.max, 4)}". The part is at ${fixed(measured, 4)}".`,
          })),
          {
            label: 'Right on the low limit',
            trap: `The low limit is ${fixed(band.min, 4)}". The part is not sitting on it.`,
          },
        ]),
      };
    },
  },
  {
    id: 'tol.fit',
    topicId: 'tol',
    skill: 'Naming a fit',
    gen: (rng) => {
      const hole = rng.step(0.5, 2, 0.25);
      const gap = rng.pick([0.0005, 0.001, 0.002, -0.0005, -0.001]);
      const shaft = Math.round((hole - gap) * 10000) / 10000;
      const solved = fit(hole, shaft);
      const clearance = solved.value.type === 'clearance';
      const size = fixed(Math.abs(solved.value.allowance), 4);

      return {
        kind: 'mc',
        prompt: `Hole is ${fixed(hole, 4)}", shaft is ${fixed(shaft, 4)}". What have you got?`,
        formula: 'allowance = hole − shaft',
        why: `${fixed(hole, 4)} − ${fixed(shaft, 4)} = ${clearance ? '' : '−'}${size}". ${clearance ? 'The shaft is smaller, so there is room. That is clearance.' : 'The shaft is bigger, so it has to be forced. That is interference.'}`,
        steps: solved.steps,
        ...mcOptions(rng, `${size}" ${clearance ? 'clearance' : 'interference'}`, [
          {
            label: `${size}" ${clearance ? 'interference' : 'clearance'}`,
            trap: 'Right amount, wrong direction. Hole minus shaft — a positive answer means room, a negative one means a squeeze.',
          },
          {
            label: `${fixed(Math.abs(solved.value.allowance) / 2, 4)}" ${clearance ? 'clearance' : 'interference'}`,
            trap: 'You halved it. That per-side habit belongs to turning, not to comparing two finished sizes.',
          },
          {
            label: 'Same size, no fit',
            trap: `They differ by ${size}". On a fit, that is the whole story.`,
          },
        ]),
      };
    },
  },
];
